import React, { memo, useCallback } from 'react';
import { pick } from 'ramda';
import { useForm } from 'react-final-form';
import { Button } from 'js/components/common/Button';
import { useShowNotification } from 'js/hooks/useShowNotification';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { useComponentConfig, useComponentTranslations } from 'js/hooks/component';

import { useDynamicReducerSelector } from '../../store';
import { useFieldLabel } from '../ChangeFormField/hooks';

import style from './Controls.scss';

export const ControlsBare: React.FC<{
    currentStep: number;
    disabled: boolean;
    handleSubmit: React.MouseEventHandler;
    setCurrentStepIndex: React.Dispatch<React.SetStateAction<number>>;
}> = ({ currentStep, disabled, setCurrentStepIndex, handleSubmit }) => {
    const formAPI = useForm();
    const { componentId } = useComponentConfig();
    const translate = useComponentTranslations();
    const showNotification = useShowNotification();
    const { steps, backButtonName, nextButtonName, submitButtonName, formId, formName } = useDynamicReducerSelector(pick([
        'steps', 'backButtonName', 'nextButtonName', 'submitButtonName', 'formId', 'formName'
    ]));
    const fieldId = `${componentId}_${formId}`;
    const [nextButtonText] = useFieldLabel(nextButtonName);
    const [backButtonText] = useFieldLabel(backButtonName);
    const [submitButtonText] = useFieldLabel(submitButtonName);

    const onNextButtonClick = useCallback(() => {
        setCurrentStepIndex((prevStepIndex: number) => prevStepIndex + 1);
        publicEventsAPIService.trigger('formStepChanged', {
            formName, formStep: currentStep + 1, totalSteps: steps.length
        });
    }, [currentStep, formName, steps.length, setCurrentStepIndex]);

    const onBackButtonClick = useCallback(() => {
        setCurrentStepIndex((prevStepIndex: number) => prevStepIndex - 1);
        publicEventsAPIService.trigger('formStepChanged', {
            formName, formStep: currentStep - 1, totalSteps: steps.length
        });
    }, [currentStep, formName, steps.length, setCurrentStepIndex]);

    const onSubmit = useCallback(async (event: React.MouseEvent<Element, MouseEvent>) => {
        handleSubmit(event);

        if (formAPI.getState().invalid) {
            showNotification(translate('playtech.mobile.system.form.validation.error'), { variant: 'error' });
        }
    }, [handleSubmit, formAPI, showNotification, translate]);

    return (
        <div className={style.controlsContainer}>
            {currentStep > 1 && backButtonName && (
                <Button
                    type="button"
                    disabled={disabled}
                    onClick={onBackButtonClick}
                    id={`${fieldId}_backButton`}
                    key={`${fieldId}_backButton`}
                >
                    {backButtonText}
                </Button>
            )}
            {currentStep !== steps.length && nextButtonName && (
                <Button
                    type="button"
                    disabled={disabled}
                    onClick={onNextButtonClick}
                    id={`${fieldId}_nextButton`}
                    key={`${fieldId}_nextButton`}
                >
                    {nextButtonText}
                </Button>
            )}
            {currentStep === steps.length && submitButtonName && (
                <Button
                    type="button"
                    onClick={onSubmit}
                    disabled={disabled}
                    id={`${fieldId}_submitButton`}
                    key={`${fieldId}_submitButton`}
                >
                    {submitButtonText}
                </Button>
            )}
        </div>
    );
};

export const Controls = memo(ControlsBare);
